import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export interface SmsConfig {
  from: string | null;
}

export interface SendAdminSmsPayload {
  mobile_number: string;
  body: string;
}

export interface SendAdminSmsResult {
  sid: string;
  to: string;
  status: string;
}

export type TwilioContact =
  | { type: "user"; name: string; email: string | null; organization: string | null }
  | {
      type: "recipient";
      name: string;
      order_number: number;
      sender_name: string | null;
      sent_on: string;
    };

export interface TwilioMessageRow {
  sid: string;
  direction: "inbound" | "outbound";
  direction_detail: string;
  from: string;
  to: string;
  counterpart: string;
  contact: TwilioContact | null;
  body: string;
  status: string;
  segments: number;
  media: number;
  price: number | null;
  price_unit: string | null;
  error_code: number | null;
  error_message: string | null;
  date_created: string | null;
  date_sent: string | null;
  date_updated: string | null;
  messaging_service_sid: string | null;
}

export interface TwilioAccountSummary {
  sid: string | null;
  phone_number: string | null;
  friendly_name: string | null;
  status: string | null;
  type: string | null;
  balance: number | null;
  currency: string | null;
  error: string | null;
}

export interface TwilioReport {
  account: TwilioAccountSummary;
  range: { from_date: string; to_date: string };
  limit: number;
  truncated: boolean;
  messages: TwilioMessageRow[];
}

export interface TwilioReportQuery {
  from_date: string;
  to_date: string;
  limit: number;
}

export const smsApi = createApi({
  reducerPath: "smsApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getSmsConfig: builder.query<Response<SmsConfig>, void>({
      query: () => ({
        url: "admin/sms/config",
        method: "GET",
      }),
    }),
    sendSms: builder.mutation<Response<SendAdminSmsResult>, SendAdminSmsPayload>({
      query: (data) => ({
        url: "admin/sms/send",
        method: "POST",
        data,
      }),
    }),
    getTwilioReport: builder.query<Response<TwilioReport>, TwilioReportQuery>({
      query: (params) => ({
        url: "admin/reports/twilio",
        method: "GET",
        params,
      }),
    }),
  }),
});

export const { useGetSmsConfigQuery, useSendSmsMutation, useGetTwilioReportQuery } = smsApi;
